import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Shield, Globe, Users, Clock, Mail, Phone, MapPin, ExternalLink, ScrollText, Activity } from 'lucide-react';

const TermsOfUse = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('general');

  const sections = [
    { id: 'general', title: 'General Terms', icon: ScrollText },
    { id: 'sla', title: 'Service Level Agreement', icon: Activity },
    { id: 'user-conduct', title: 'User Responsibilities', icon: Users },
    { id: 'security', title: 'Security & Data', icon: Shield },
    { id: 'intellectual-property', title: 'Intellectual Property', icon: Shield },
    { id: 'legal', title: 'Liability & Governing Law', icon: Clock }
  ];

  const scrollToSection = (id) => {
    setActiveTab(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-[#f9fafb] text-[#374151] font-sans selection:bg-[#3aadd6] selection:text-white">
      {/* Professional Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <img 
                  src="/S-Track_UC.svg" 
                  alt="S-Track Logo" 
                  className="h-16 w-auto object-contain" 
                  style={{ transform: "scale(2.1) translateY(4px)", transformOrigin: "left center" }} 
                />
              </div>
            </div>
            <button 
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 text-[#2a4888] hover:text-[#3aadd6] font-medium transition-colors group"
            >
              <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
              <span>Return to Login</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Layout */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">
          
          {/* Sidebar Navigation */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24 space-y-1.5">
              <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-widest px-4 mb-2">Documentation</h3>
              {sections.map((section) => (
                <button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm font-medium rounded-lg transition-all ${
                    activeTab === section.id 
                    ? 'bg-[#3aadd6]/10 text-[#2a4888] border-l-4 border-[#3aadd6]' 
                    : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <section.icon size={18} className={activeTab === section.id ? 'text-[#3aadd6]' : 'text-gray-400'} />
                  {section.title}
                </button>
              ))}
              
              <div className="mt-6 p-5 bg-white border border-gray-100 rounded-xl shadow-sm">
                <p className="text-[11px] text-gray-400 leading-relaxed italic">
                  "S-Track ITSM TICKETING TOOL is the product of Smac-X Inno Labs."
                </p>
              </div>
            </div>
          </aside>

          {/* Detailed Content */}
          <main className="lg:col-span-3 space-y-8 pb-12">
            
            {/* Intro */}
            <div className="max-w-3xl">
              <h1 className="text-3xl font-extrabold text-[#2a4888] tracking-tight mb-3">Terms of Service</h1>
              <p className="text-md text-gray-500 leading-relaxed">
                Thank you for choosing S-Track, the premium ITSM Ticketing solution by Smac-X Inno Labs. 
                These terms govern your use of our enterprise-grade ticketing platform and service commitments.
              </p>
            </div>

            {/* General Terms */}
            <section id="general" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <ScrollText size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">1. General Terms</h2>
              </div>
              <div className="prose prose-slate max-w-none text-gray-600 leading-relaxed text-sm">
                <p>
                  By accessing S-Track, you agree to these Terms of Service. If you are entering into this agreement on behalf of a company or other legal entity, 
                  you represent that you have the authority to bind such entity and its affiliates to these terms.
                </p>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  <li><strong>Agreement Scope:</strong> Governs the use of S-Track ITSM modules and ticketing workflows.</li>
                  <li><strong>Modifications:</strong> We reserve the right to update these terms with prior notice.</li>
                </ul>
              </div>
            </section>

            {/* SLA Section */}
            <section id="sla" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <Activity size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">2. Service Level Agreement (SLA)</h2>
              </div>
              <div className="text-gray-600 leading-relaxed space-y-3 text-sm">
                <p>
                  Our Service Level Agreement outlines our commitment to providing a reliable and responsive ITSM environment. 
                  S-Track is designed to meet high availability standards for mission-critical operations.
                </p>
                <div className="bg-blue-50/50 border border-blue-100 rounded-xl p-5 grid md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-4">
                    <Clock className="text-[#3aadd6] mt-1 shrink-0" size={20} />
                    <div>
                      <h4 className="font-bold text-[#2a4888] mb-0.5">Uptime Commitment</h4>
                      <p className="text-xs">We aim for 99.9% platform availability, ensuring your ticketing system is always ready when you need it.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Activity className="text-[#3aadd6] mt-1 shrink-0" size={20} />
                    <div>
                      <h4 className="font-bold text-[#2a4888] mb-0.5">Response Times</h4>
                      <p className="text-xs">Technical support issues are prioritized based on severity, with rapid response goals for critical system interruptions.</p>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* User Responsibilities */}
            <section id="user-conduct" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <Users size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">3. User Responsibilities</h2>
              </div>
              <div className="text-gray-600 leading-relaxed space-y-3 text-sm">
                <p>Users are expected to maintain professional conduct while using the ITSM environment. Responsibilities include:</p>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="p-4 border border-gray-100 rounded-lg bg-white">
                    <h5 className="font-bold text-sm text-gray-900 mb-1">Account Security</h5>
                    <p className="text-xs">Maintaining the confidentiality of passwords and API keys.</p>
                  </div>
                  <div className="p-4 border border-gray-100 rounded-lg bg-white">
                    <h5 className="font-bold text-sm text-gray-900 mb-1">Data Accuracy</h5>
                    <p className="text-xs">Ensuring ticket information and user profiles are accurate.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Security & Data */}
            <section id="security" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <Shield size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">4. Security & Data Privacy</h2>
              </div>
              <div className="text-gray-600 leading-relaxed space-y-3 text-sm">
                <p>
                  Data security is our top priority. We implement industry-standard encryption and security protocols to protect your 
                  ITSM data within our infrastructure.
                </p>
                <div className="bg-slate-50 rounded-xl p-6 border border-gray-200">
                  <h4 className="font-bold text-gray-900 mb-3">Our Commitment</h4>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-3 text-xs">
                      <div className="w-1 h-1 rounded-full bg-[#3aadd6]"></div>
                      GDPR and international data protection compliance.
                    </li>
                    <li className="flex items-center gap-3 text-xs">
                      <div className="w-1 h-1 rounded-full bg-[#3aadd6]"></div>
                      Regular security audits and vulnerability assessments.
                    </li>
                    <li className="flex items-center gap-3 text-xs">
                      <div className="w-1 h-1 rounded-full bg-[#3aadd6]"></div>
                      Encrypted data transfers and storage.
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Intellectual Property */}
            <section id="intellectual-property" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <Shield size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">5. Intellectual Property</h2>
              </div>
              <p className="text-gray-600 leading-relaxed text-sm">
                All rights, title, and interest in and to S-Track, including all related intellectual property rights, 
                are and will remain the exclusive property of <strong>Smac-X Inno Labs Private Limited</strong>.
              </p>
            </section>

            {/* Legal */}
            <section id="legal" className="scroll-mt-24">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[#3aadd6] flex items-center justify-center text-white">
                  <Clock size={18} />
                </div>
                <h2 className="text-xl font-bold text-gray-900">6. Liability & Governing Law</h2>
              </div>
              <div className="text-gray-600 leading-relaxed space-y-3 text-sm">
                <p>
                  To the maximum extent permitted by law, Smac-X Inno Labs shall not be liable for any indirect, incidental, 
                  special, consequential, or punitive damages.
                </p>
                <p>
                  These terms shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law principles.
                </p>
              </div>
            </section>

            {/* Contact Section */}
            <section className="pt-6 border-t border-gray-100">
              <div className="bg-[#2a4888] rounded-2xl p-6 text-white flex flex-col md:flex-row justify-between items-center gap-6">
                <div>
                  <h3 className="text-lg font-bold mb-1">Need Compliance Help?</h3>
                  <p className="text-gray-300 text-xs">Our legal and support teams are available for any questions.</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href="mailto:support@smac-x.com" className="flex items-center gap-2 bg-[#3aadd6] hover:bg-[#3aadd6]/90 px-5 py-2.5 rounded-lg font-bold transition-all shadow-lg shadow-[#3aadd6]/20 text-sm">
                    <Mail size={16} />
                    support@smac-x.com
                  </a>
                </div>
              </div>
            </section>

          </main>
        </div>
      </div>

      {/* Modern Footer */}
      <footer className="bg-white border-t border-gray-200 py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center items-center gap-2 mb-1.5">
            <img src="/S-Track_UC.svg" alt="S-Track Logo" className="h-16 w-auto object-contain" />
            <span className="text-gray-300">|</span>
            <span className="text-gray-400 text-sm">Smac-X Inno Labs</span>
          </div>
          <p className="text-gray-400 text-sm">
            © 2026 Smac-X Inno Labs Private Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

export default TermsOfUse;